import { NavLink, Outlet, Link } from 'react-router-dom';
import { authConfigured, useAuth } from '../lib/auth';
import { Alert, LoadingBlock } from '../components/ui';

/**
 * Gates the admin portal. Renders a sign-in prompt when there is no session and
 * an explanatory block when the account lacks the admins group. The API
 * enforces the same rules independently.
 */
export function AdminLayout() {
  const { ready, user, isAdmin, email, error, signIn, signOut } = useAuth();

  if (!authConfigured) {
    return (
      <ShellFrame>
        <Alert variant="error" title="Admin sign-in is not configured">
          <p>
            This build has no Cognito settings. Set <code>VITE_COGNITO_AUTHORITY</code> and{' '}
            <code>VITE_COGNITO_CLIENT_ID</code> and rebuild the frontend. The deploy script writes
            these from the stack outputs automatically.
          </p>
        </Alert>
      </ShellFrame>
    );
  }

  if (!ready) return <ShellFrame><LoadingBlock message="Checking your session..." /></ShellFrame>;

  if (!user || user.expired) {
    return (
      <ShellFrame>
        <div className="card stack" style={{ maxWidth: '32rem', margin: '0 auto' }}>
          <h1>Organiser sign-in</h1>
          <p className="muted">
            The admin portal is restricted to event organisers. You will be taken to the secure
            sign-in page.
          </p>
          {error && (
            <Alert variant="error" title="Sign-in problem">
              <p style={{ marginBottom: 0 }}>{error}</p>
            </Alert>
          )}
          <div>
            <button type="button" className="btn btn-primary btn-lg" onClick={() => void signIn()}>
              Sign in
            </button>
          </div>
          <p className="faint" style={{ marginBottom: 0 }}>
            <Link to="/">Back to the public site</Link>
          </p>
        </div>
      </ShellFrame>
    );
  }

  if (!isAdmin) {
    return (
      <ShellFrame>
        <div className="card stack" style={{ maxWidth: '32rem', margin: '0 auto' }}>
          <Alert variant="error" title="Not authorised">
            <p style={{ marginBottom: 0 }}>
              {email ?? 'This account'} is signed in but is not a member of the <code>admins</code>{' '}
              group. Ask an existing organiser to invite you from the Organisers page in the
              portal.
            </p>
          </Alert>
          <div>
            <button type="button" className="btn btn-secondary" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </ShellFrame>
    );
  }

  return (
    <div className="admin">
      <a className="skip-link" href="#admin-main">
        Skip to main content
      </a>

      <header className="admin-header">
        <div className="container">
          <Link to="/admin" className="brand">
            <span className="brand-mark" aria-hidden="true">
              HB
            </span>
            <span>Event admin</span>
          </Link>

          <nav className="admin-nav" aria-label="Admin sections">
            <NavLink to="/admin" end>
              Dashboard
            </NavLink>
            <NavLink to="/admin/entries">Entries</NavLink>
            <NavLink to="/admin/heats">Heats</NavLink>
            <NavLink to="/admin/organisers">Organisers</NavLink>
            <NavLink to="/admin/settings">Settings</NavLink>
          </nav>

          <div className="admin-user">
            <span>{email}</span>
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main" id="admin-main">
        <div className="container">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function ShellFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="admin">
      <header className="admin-header">
        <div className="container">
          <Link to="/" className="brand">
            <span className="brand-mark" aria-hidden="true">
              HB
            </span>
            <span>Event admin</span>
          </Link>
        </div>
      </header>
      <main className="admin-main" id="admin-main">
        <div className="container">{children}</div>
      </main>
    </div>
  );
}
